import { z } from 'zod';
import { allocationEditScopeSchema, allocationStatusSchema, capacityHealthSchema, healthStatusSchema, nonNegativeMoneySchema, projectWorkTypeSchema, } from './common.js';
import { planningSettingsSchema } from './planning.js';
const positiveMoneySchema = nonNegativeMoneySchema.extend({
    amountMinor: z.number().int().positive(),
});
export const capacityValueRateSchema = z.object({
    hourlyRate: positiveMoneySchema.nullable(),
});
export const capacityValueRateInputSchema = capacityValueRateSchema.pick({
    hourlyRate: true,
});
export const expertiseSchema = z.object({
    id: z.uuid(),
    name: z.string().min(1).max(120),
    active: z.boolean(),
});
export const expertiseInputSchema = z.object({
    name: z.string().trim().min(1).max(120),
    active: z.boolean().default(true),
});
const allocationBaseInputSchema = z.object({
    projectId: z.uuid(),
    resourceId: z.uuid().nullable().default(null),
    expertiseId: z.uuid().nullable().default(null),
    status: allocationStatusSchema.default('hard'),
    startWeek: z.iso.date(),
    endWeek: z.iso.date(),
    notes: z.string().trim().max(1_000).nullable().default(null),
});
function validateAllocationOwner(value, context) {
    if ((value.resourceId === null) === (value.expertiseId === null)) {
        context.addIssue({
            code: 'custom',
            message: 'Exactly one of resourceId or expertiseId is required.',
            path: ['resourceId'],
        });
    }
}
export const allocationInputSchema = z
    .discriminatedUnion('entryMode', [
    allocationBaseInputSchema.extend({
        entryMode: z.literal('hours'),
        weeklyMinutes: z
            .number()
            .int()
            .min(0)
            .max(7 * 24 * 60),
        percentageBasisPoints: z.null().optional(),
    }),
    allocationBaseInputSchema.extend({
        entryMode: z.literal('percentage'),
        weeklyMinutes: z.null().optional(),
        percentageBasisPoints: z.number().int().min(0).max(100_000),
    }),
])
    .superRefine((value, context) => validateAllocationOwner(value, context));
export const allocationUpdateSchema = z.object({
    scope: allocationEditScopeSchema,
    targetWeek: z.iso.date(),
    weeklyMinutes: z
        .number()
        .int()
        .min(0)
        .max(7 * 24 * 60),
    status: allocationStatusSchema.optional(),
    notes: z.string().trim().max(1_000).nullable().optional(),
    resourceId: z.uuid().optional(),
});
export const allocationDeleteInputSchema = z
    .object({
    reasonCode: z.enum([
        'data_entry_error',
        'client_delay',
        'novatize_delay',
        'scope_cancelled',
        'project_cancelled',
        'deferred_load',
        'capacity_unavailable',
        'other',
    ]),
    reasonDetail: z
        .string()
        .trim()
        .min(1)
        .max(500)
        .optional()
        .describe('Required when reasonCode is other.'),
})
    .superRefine((value, context) => {
    if (value.reasonCode === 'other' && !value.reasonDetail) {
        context.addIssue({
            code: 'custom',
            message: 'A detail is required when the deletion reason is other.',
            path: ['reasonDetail'],
        });
    }
});
export const dailyAllocationBulkDeleteSchema = z.object({
    selections: z
        .array(z
        .object({
        seriesId: z.uuid(),
        startDate: z.iso.date(),
        endDate: z.iso.date(),
        expectedMinutes: z.number().int().nonnegative(),
        expectedUpdatedAt: z.iso.datetime().optional(),
    })
        .refine((value) => value.startDate <= value.endDate))
        .min(1)
        .max(500),
    reason: allocationDeleteInputSchema,
});
export const dailyAllocationRescheduleSchema = z
    .object({
    scope: z.enum(['period', 'series']),
    periodStart: z.iso.date(),
    periodEnd: z.iso.date(),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    totalMinutes: z.number().int().positive().max(10_000_000),
    expectedMinutes: z.number().int().nonnegative(),
    expectedUpdatedAt: z.iso.datetime().optional(),
})
    .refine((value) => value.periodStart <= value.periodEnd && value.startDate <= value.endDate);
export const allocationStaffingInputSchema = z.object({
    resourceId: z.uuid(),
    effectiveWeek: z.iso.date(),
});
const dailyAllocationBaseSchema = z.object({
    projectId: z.uuid(),
    resourceId: z.uuid().nullable().default(null),
    expertiseId: z.uuid().nullable().default(null),
    status: allocationStatusSchema.default('hard'),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    notes: z.string().trim().max(1_000).nullable().default(null),
});
export const dailyAllocationInputSchema = z
    .discriminatedUnion('entryMode', [
    dailyAllocationBaseSchema.extend({
        entryMode: z.literal('total_hours'),
        totalMinutes: z.number().int().positive().max(10_000_000),
        percentageBasisPoints: z.null().optional(),
    }),
    dailyAllocationBaseSchema.extend({
        entryMode: z.literal('capacity_percentage'),
        totalMinutes: z.null().optional(),
        percentageBasisPoints: z.number().int().positive().max(100_000),
    }),
])
    .superRefine((value, context) => {
    validateAllocationOwner(value, context);
    if (value.startDate > value.endDate) {
        context.addIssue({
            code: 'custom',
            message: 'Allocation end date must follow its start date.',
            path: ['endDate'],
        });
    }
});
export const dailyAllocationStaffingInputSchema = z
    .object({
    resourceId: z.uuid(),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    totalMinutes: z.number().int().positive().max(10_000_000),
})
    .refine((value) => value.startDate <= value.endDate, {
    message: 'Staffing end date must follow its start date.',
    path: ['endDate'],
});
export const dailyAllocationUpdateSchema = z
    .object({
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    totalMinutes: z.number().int().positive().max(10_000_000),
    status: allocationStatusSchema.optional(),
    resourceId: z.uuid().optional(),
})
    .refine((value) => value.startDate <= value.endDate, {
    message: 'Allocation end date must follow its start date.',
    path: ['endDate'],
});
export const dailyAllocationDeleteQuerySchema = z
    .object({
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    scope: z.enum(['period', 'series']).default('period'),
})
    .refine((value) => value.startDate <= value.endDate, {
    message: 'Allocation end date must follow its start date.',
    path: ['endDate'],
});
export const capacityAllocationBreakdownSchema = z.object({
    seriesId: z.uuid(),
    projectId: z.uuid(),
    projectName: z.string(),
    projectWorkType: projectWorkTypeSchema,
    status: allocationStatusSchema,
    source: z.enum(['manual', 'jira_demand']),
    jiraIssueKey: z.string().nullable().optional(),
    jiraIssueTitle: z.string().nullable().optional(),
    subjectType: z.enum(['resource', 'expertise']),
    plannedMinutes: z.number().int().nonnegative(),
    seriesTotalMinutes: z.number().int().nonnegative().nullable().optional(),
    seriesStartDate: z.iso.date().nullable().optional(),
    seriesEndDate: z.iso.date().nullable().optional(),
    seriesUpdatedAt: z.iso.datetime().optional(),
});
export const capacityGranularitySchema = z.enum(['day', 'week', 'sprint', 'month']);
export const capacityCalendarPeriodSchema = z.object({
    key: z.string(),
    label: z.string(),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
});
export const capacityCalendarCellSchema = z.object({
    period: capacityCalendarPeriodSchema,
    capacityMinutes: z.number().int().nonnegative(),
    officialMinutes: z.number().int().nonnegative(),
    provisionalMinutes: z.number().int().nonnegative(),
    totalLoadMinutes: z.number().int().nonnegative().optional(),
    jiraMinutes: z.number().int().nonnegative(),
    remainingMinutes: z.number().int(),
    allocations: z.array(capacityAllocationBreakdownSchema),
});
export const capacityCalendarSchema = z.object({
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    granularity: capacityGranularitySchema,
    summary: z.object({
        availableMinutes: z.number().int().nonnegative(),
        confirmedAllocatedMinutes: z.number().int().nonnegative(),
        projectedAllocatedMinutes: z.number().int().nonnegative(),
        confirmedDeficitMinutes: z.number().int().nonnegative(),
        projectedDeficitMinutes: z.number().int().nonnegative(),
        budgetedConfirmedMinutes: z.number().int().nonnegative(),
        budgetedProjectedMinutes: z.number().int().nonnegative(),
        potentialValue: nonNegativeMoneySchema.nullable(),
        potentialValueState: z.enum(['available', 'unconfigured', 'restricted']),
    }),
    periods: z.array(capacityCalendarPeriodSchema),
    people: z.array(z.object({
        resourceId: z.uuid(),
        name: z.string(),
        primaryExpertise: expertiseSchema.nullable(),
        teamNames: z.array(z.string()),
        tempoTeams: z.array(z.string()),
        tempoTeamIds: z.array(z.string()).optional(),
        periods: z.array(capacityCalendarCellSchema),
    })),
    placeholders: z.array(z.object({
        expertise: expertiseSchema,
        periods: z.array(capacityCalendarCellSchema),
    })),
});
export const capacityWeekSchema = z.object({
    weekStart: z.iso.date(),
    capacityMinutes: z.number().int().nonnegative(),
    billableTargetMinutes: z.number().int().nonnegative(),
    officialMinutes: z.number().int().nonnegative(),
    provisionalMinutes: z.number().int().nonnegative(),
    billableMinutes: z.number().int().nonnegative(),
    remainingMinutes: z.number().int(),
    health: capacityHealthSchema,
    allocations: z.array(capacityAllocationBreakdownSchema),
});
export const capacityPersonSchema = z.object({
    resourceId: z.uuid(),
    name: z.string(),
    primaryExpertise: expertiseSchema.nullable(),
    teamNames: z.array(z.string()),
    tempoTeams: z.array(z.string()),
    weeks: z.array(capacityWeekSchema),
});
export const tempoRosterStatusSchema = z.object({
    available: z.boolean().nullable(),
    currentMemberCount: z.number().int().nonnegative().nullable(),
    synchronizedAt: z.iso.datetime().nullable(),
    errorCode: z.string().nullable(),
});
export const capacityOverviewSchema = z.object({
    startWeek: z.iso.date(),
    weekCount: z.union([z.literal(4), z.literal(12), z.literal(26)]),
    settings: planningSettingsSchema,
    people: z.array(capacityPersonSchema),
    rosterStatus: tempoRosterStatusSchema,
    currentSprint: z.object({
        startDate: z.iso.date(),
        endDate: z.iso.date(),
        overloadedResourceIds: z.array(z.uuid()),
    }),
    placeholders: z.array(z.object({
        expertise: expertiseSchema,
        weeks: z.array(capacityWeekSchema),
    })),
});
export const capacityPointSchema = z.object({
    subjectId: z.string(),
    subjectName: z.string(),
    date: z.iso.date(),
    capacityMinutes: z.number().int().nonnegative(),
    demandMinutes: z.number().int().nonnegative(),
    occupancyPercent: z.number().nonnegative(),
    health: healthStatusSchema,
});
export const allocationDeletionSchema = z.object({
    id: z.uuid(),
    seriesId: z.uuid().nullable(),
    projectId: z.uuid(),
    projectName: z.string(),
    subjectName: z.string(),
    actorName: z.string(),
    removedMinutes: z.number().int().nonnegative().nullable(),
    reasonCode: z.string(),
    reasonDetail: z.string().nullable(),
    deletedAt: z.iso.datetime(),
    restoredAt: z.iso.datetime().nullable(),
    source: z.enum(['snapshot', 'legacy_audit']),
    canRestore: z.boolean(),
    restoreUnavailableReason: z.string().nullable(),
});
export const allocationDeletionsQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(25),
    projectId: z.uuid().optional(),
});
export const allocationDeletionsResponseSchema = z.object({
    data: z.array(allocationDeletionSchema),
    meta: z.object({
        page: z.number().int().positive(),
        pageSize: z.number().int().positive(),
        total: z.number().int().nonnegative(),
    }),
});
export const allocationRestoreResponseSchema = z.object({
    data: z.object({ id: z.uuid(), alreadyRestored: z.boolean() }),
});
//# sourceMappingURL=capacity.js.map